import { isEmpty } from 'lodash-es';
import { type MouseEvent as ReactMouseEvent, useState } from 'react';

import { DownloadDataButton } from './DownloadDataButton.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { A11yToolbarTabIndex } from '../../../core/types/toolbar.js';
import { zIndicesCategoricalAreas } from '../../constants.js';
import { useMouseInBoundsState } from '../../hooks/store/selectors.js';
import { useCategoricalBarChartBaseConfig } from '../../hooks/useCategoricalBarChartBaseConfig.js';
import { useCategoricalBarChartRawData } from '../../hooks/useCategoricalBarChartRawData.js';
import { buildAndDownloadCsv } from '../../utils/build-and-download-csv.js';

const defaultToolbarPlacement = 'top-right';

type CategoricalBarChartToolbarProps = {
  onMouseEnter: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
};

export const CategoricalBarChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
}: CategoricalBarChartToolbarProps) => {
  const [isTooltipHidden, setIsTooltipHidden] = useState(false);
  const { toolbar } = useCategoricalBarChartBaseConfig();
  const isInChartBounds = useMouseInBoundsState();

  const isDownloadEnabled = toolbar?.downloadData?.enabled ?? false;

  const isVisible = isInChartBounds && !isEmpty(toolbar);

  const handleToggleMenu = (open: boolean): void => {
    !open && onMouseLeave();
  };

  const rawData = useCategoricalBarChartRawData();

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () => buildAndDownloadCsv(rawData);

  const showDefaultToolbarDownloadButton =
    isDownloadEnabled && !toolbar?.intents;

  const initialA11yState: A11yToolbarTabIndex[] =
    showDefaultToolbarDownloadButton ? [{ download: 0 }] : [{ intent: 0 }];

  return (
    <ChartToolbarRenderer
      visible={isVisible}
      placement={toolbar.placement || defaultToolbarPlacement}
      collapsed={toolbar.collapsed}
      draggable={toolbar.draggable}
      zIndex={zIndicesCategoricalAreas.toolbar}
      initialA11yState={initialA11yState}
      onToggleToolbar={setIsTooltipHidden}
      onToggleMenu={handleToggleMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuDisplayMode={'never'}
      intents={toolbar.intents}
      isDownloadEnabled={isDownloadEnabled}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      DownloadButton={() => (
        <DownloadDataButton hideTooltip={isTooltipHidden} />
      )}
    >
      {showDefaultToolbarDownloadButton && (
        <DownloadDataButton hideTooltip={isTooltipHidden} />
      )}
    </ChartToolbarRenderer>
  );
};
